import { Box, styled, Tooltip, type SxProps, type Theme } from '@mui/material';
import type { Provider } from '@repo/api-gw-sdk';

import { CloudProviders } from '@/data/inventory/data';
import type { BackupVault } from '@/data/vaults/backupVault';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { NativeVaultColor } from '@/theme/colorSchemes';

import { BackupVaultCard, VaultCardAsAccount } from './backupVaultCard';

const Tag = styled('div')(
  (props: { backgroundColor: string; additionalStyles?: object }) => ({
    cursor: 'default',
    borderRadius: '4px',
    fontSize: '12px',
    padding: '2px 8px',
    margin: '2px 4px',
    color: 'black',
    backgroundColor: props.backgroundColor,
    display: 'inline-block',
    ...props.additionalStyles,
  })
);

function VaultTagFromId({ vaultId }: { vaultId: string }) {
  const { vaults } = useBackupVaults();
  const vault = vaults.find((vault) => vault.id === vaultId);
  if (!vault) {
    return null;
  }

  return <VaultTag vault={vault} />;
}

export function VaultTag({
  sx,
  vault,
}: {
  sx?: SxProps<Theme>;
  vault: BackupVault | string;
}) {
  if (typeof vault === 'string') {
    return <VaultTagFromId vaultId={vault} />;
  }

  return <BackupVaultTag sx={sx} vault={vault} />;
}

export const AccountAsVaultTag = ({
  sx,
  cloudProvider,
  region,
}: {
  sx?: SxProps<Theme>;
  cloudProvider: Provider;
  region: string;
}) => {
  return (
    <VaultTagInner
      sx={sx}
      backgroundColor={NativeVaultColor}
      name='Source account'
      cloudProvider={cloudProvider}
      vaultCard={
        <VaultCardAsAccount
          sx={sx}
          cloudProvider={cloudProvider}
          region={region}
        />
      }
    />
  );
};

const BackupVaultTag = ({
  sx,
  vault,
}: {
  sx?: SxProps<Theme>;
  vault: BackupVault;
}) => {
  return (
    <VaultTagInner
      sx={sx}
      backgroundColor={vault.backgroundColor || 'black'}
      name={vault.name}
      cloudProvider={vault.vaultAttributes.cloudProvider}
      vaultCard={<BackupVaultCard data={vault} editable={false} />}
    />
  );
};

const VaultTagInner = ({
  sx,
  backgroundColor,
  name,
  cloudProvider,
  vaultCard,
}: {
  sx?: SxProps<Theme>;
  backgroundColor: string;
  name: string;
  cloudProvider: string;
  vaultCard: React.ReactNode;
}) => {
  return (
    <Tooltip
      disableInteractive={false}
      disableFocusListener
      PopperProps={{
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              altAxis: true,
              padding: 8,
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top', 'bottom', 'left', 'right'],
            },
          },
        ],
      }}
      slotProps={{
        tooltip: {
          sx: {
            padding: 0,
            backgroundColor: 'transparent',
            maxWidth: '300px',
          },
        },
      }}
      title={vaultCard}
    >
      <Tag
        data-testid='vault-tag'
        additionalStyles={{
          ...sx,
          maxWidth: '140px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        backgroundColor={backgroundColor || 'black'}
      >
        <Box component={'span'} className='font-semibold mr-[4px]'>
          {CloudProviders[cloudProvider]?.displayName}
        </Box>
        <Box component={'span'}>{name}</Box>
      </Tag>
    </Tooltip>
  );
};
